/* eslint react/jsx-no-duplicate-props: 0 */
import React, { PureComponent, Requireable, useState, Validator } from 'react';
import PropTypes from 'prop-types';
import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle as Check,
  faExclamationCircle as Required,
  faEye,
  faEyeSlash,
} from '@fortawesome/pro-light-svg-icons';
import { combineClasses } from '../../utils/styling';
import { withNkStyles } from '../../infrastructure/theme';

type NodeValidator = PropTypes.Validator<React.ReactNode>;
type FunctionType = (...args: any[]) => any;
type FunctionValidator = Validator<FunctionType>;
type FunctionRequireable = Requireable<FunctionType>;
type TextFieldVariant = 'filled' | 'standard' | 'outlined' | undefined;

interface NkInputProps {
  name: string;
  value: React.ReactNode;
  propName: string;
  onChange: FunctionType;
  onBlur?: FunctionType;
  fullWidth?: boolean;
  margin?: any;
  inputComponent?: FunctionType;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  isRequired?: boolean;
  password?: boolean;
  label?: string;
  classes?: any;
  variant?: TextFieldVariant;
  placeholder?: string;
  helperText?: string;
  errorText?: string;
}

/**
 * @deprecated Use `_NkInput` instead.
 */
class NkInput extends PureComponent<NkInputProps> {
  static propTypes: {
    name: Validator<string>;
    value: NodeValidator;
    propName: Validator<string>;
    onChange: FunctionValidator;
    onBlur: FunctionRequireable;
    fullWidth: Requireable<boolean>;
    margin: any;
    inputComponent: FunctionRequireable;
    disabled: Requireable<boolean>;
    error: Requireable<boolean>;
    required: Requireable<boolean>;
    isRequired: Requireable<boolean>;
    password: Requireable<boolean>;
    label: Requireable<string>;
    classes: any;
    variant: any;
    placeholder: Requireable<string>;
    errorText: Requireable<string>;
    helperText: Requireable<string>;
  };

  render() {
    const { fullWidth, margin, classes, password } = this.props;
    const _fullWidth = fullWidth || true;
    const _margin = margin || 'dense';
    const _type = password ? 'password' : 'text';

    return (
      <FormControl fullWidth={_fullWidth} className={classes.formControl}>
        <TextField
          variant={this.props.variant}
          fullWidth={_fullWidth}
          margin={_margin}
          type={_type}
          name={this.props.name}
          id={this.props.name}
          value={this.props.value}
          label={this.props.label}
          onChange={this.props.onChange(this.props.propName)}
          disabled={this.props.disabled}
          error={this.props.error}
          required={this.props.isRequired}
          onBlur={this.props.onBlur}
          placeholder={this.props.placeholder}
          helperText={this.props.errorText}
          inputProps={{
            className: classes.input,
          }}
          InputProps={{
            inputComponent: this.props.inputComponent,
          }}
          InputLabelProps={{
            className: 'input-label',
            classes: {
              shrink: classes.inputLabelShrink,
            },
          }}
          FormHelperTextProps={{
            className: !this.props.error
              ? combineClasses([
                  classes.validFieldText,
                  classes.fieldHelperText,
                ])
              : combineClasses([classes.errorText, classes.fieldHelperText]),
          }}
        />
      </FormControl>
    );
  }
}

NkInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  propName: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  fullWidth: PropTypes.bool,
  margin: PropTypes.oneOf(['normal', 'dense', 'none']),
  inputComponent: PropTypes.func,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  required: PropTypes.bool,
  isRequired: PropTypes.bool,
  password: PropTypes.bool,
  label: PropTypes.string,
  classes: PropTypes.object,
  variant: PropTypes.oneOf(['filled', 'standard', 'outlined']),
  placeholder: PropTypes.string,
  errorText: PropTypes.string,
  helperText: PropTypes.string
};

export default withNkStyles(NkInput);

interface _NkInputProps extends NkInputProps {
  isValid?: boolean;
  readOnly?: any;
  inputClasses?: any;
  type?: string;
  autoFocus?: boolean;
  min?: number;
  max?: number;
  className?: string;
}

export const _NkInput = withNkStyles(
  ({
    classes,
    disabled,
    error,
    required,
    isRequired,
    fullWidth,
    inputComponent,
    isValid,
    label,
    margin,
    name,
    onBlur,
    onChange,
    password,
    readOnly,
    value,
    variant,
    placeholder,
    errorText,
    helperText,
    inputClasses,
    autoFocus,
    type,
    min,
    max,
    className
  }: _NkInputProps) => {
    const [showPassword, setShowPassword] = useState(false);
    const _fullWidth = fullWidth || true;
    const _margin = margin || 'dense';
    const _type = password ? 'password' : type;

    return (
      <TextField
        className={className}
        autoFocus={autoFocus}
        variant={variant}
        fullWidth={_fullWidth}
        margin={_margin}
        type={password ? (showPassword ? 'text' : 'password') : _type}
        name={name}
        id={name}
        value={value}
        label={label}
        onChange={onChange}
        disabled={disabled}
        error={error}
        required={isRequired}
        onBlur={onBlur}
        helperText={errorText}
        placeholder={placeholder}
        inputProps={{
          min: min,
          max: max,
        }}
        InputProps={{
          classes: inputClasses,
          inputComponent: inputComponent,
          readOnly: readOnly || false,
          endAdornment: (
            <>
              {required && (
                <InputAdornment position="end">
                  {isValid ? (
                    <FontAwesomeIcon
                      icon={Check}
                      className={`${classes.endAdornmentIcon} ${classes.successIcon}`}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={Required}
                      className={`${classes.endAdornmentIcon} ${classes.errorIcon}`}
                    />
                  )}
                </InputAdornment>
              )}
              {password && (
                <FontAwesomeIcon
                  icon={showPassword ? faEye : faEyeSlash}
                  size="sm"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowPassword((oldValue) => !oldValue)}
                />
              )}
            </>
          ),
        }}
        FormHelperTextProps={{
          className:
            !required || isValid
              ? combineClasses([
                  classes.validFieldText,
                  classes.fieldHelperText,
                ])
              : combineClasses([classes.errorText, classes.fieldHelperText]),
        }}
      />
    );
  },
);
